"use client";

import { useId, useRef, useState } from "react";
import InvitationCard from "./InvitationCard";

const PER_PAGE = 6; // 3 columns × 2 rows

export default function InvitationGrid({ invitations = [] }) {
    const searchId = useId();
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const gridRef = useRef(null);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        // Automatically scroll back to the top of the template library section
        if (gridRef.current) {
            const section = gridRef.current.closest("section");
            if (section) {
                // Offset by 100px to account for the sticky header
                const y = section.getBoundingClientRect().top + window.scrollY - 100;
                window.scrollTo({ top: y, behavior: "smooth" });
            }
        }
    };

    const safeInvitations = Array.isArray(invitations) ? invitations : [];
    const filteredInvitations = safeInvitations.filter(
        (inv) =>
            (inv.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (inv.description || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
    const totalPages = Math.ceil(filteredInvitations.length / PER_PAGE);
    const start = (page - 1) * PER_PAGE;
    const visible = filteredInvitations.slice(start, start + PER_PAGE);

    return (
        <div ref={gridRef} className="flex flex-col gap-8">
            <div className="relative">
                <svg
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                    id={searchId}
                    type="search"
                    placeholder="Cari template..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(1);
                    }}
                    className="w-full rounded-full border border-primary-light bg-white py-3 pl-10 pr-4 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-primary focus:outline-none"
                />
            </div>
            
            {filteredInvitations.length > 0 ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {visible.map((inv, i) => (
                        <InvitationCard
                            key={inv.id}
                            invitation={inv}
                            priority={page === 1 && i < 3}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 py-16 text-center">
                    <p className="text-sm font-medium text-zinc-900">Tidak ada template ditemukan</p>
                    <p className="mt-1 text-xs text-zinc-500">
                        {searchQuery ? "Coba gunakan kata kunci lain." : "Belum ada produk digital yang tersedia saat ini."}
                    </p>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3">
                    <button
                        onClick={() => handlePageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-primary-light text-primary-dark transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-35"
                        aria-label="Halaman sebelumnya"
                    >
                        <ChevronLeftIcon />
                    </button>

                    <div className="flex gap-1.5">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <button
                                key={p}
                                onClick={() => handlePageChange(p)}
                                className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-sm font-semibold transition ${p === page
                                    ? "bg-primary text-white shadow-sm"
                                    : "border border-primary-light text-primary-dark hover:border-primary hover:text-primary"
                                    }`}
                                aria-label={`Halaman ${p}`}
                                aria-current={p === page ? "page" : undefined}
                            >
                                {p}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-primary-light text-primary-dark transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-35"
                        aria-label="Halaman berikutnya"
                    >
                        <ChevronRightIcon />
                    </button>
                </div>
            )}
        </div>
    );
}

function ChevronLeftIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="h-4 w-4">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}

function ChevronRightIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="h-4 w-4">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}
